import { useCallback } from "react";
import type { Dispatch, SetStateAction } from "react";
import { trackEvent } from "../../../../../utils/mixpanel";
import { TOOLTIP_STEPS } from "../../../../../utils/constant";
import type { TTooltipStep } from "../../../../../utils/constant";
import type { TConsultView, TSession } from "../../../../../utils/types";
import type { ConsultPatient } from "./useConsultSearch";

interface ConsultNavigationProps {
  currentView: TConsultView;
  setCurrentView: Dispatch<SetStateAction<TConsultView>>;
  selectedPatient: ConsultPatient | null;
  setSelectedPatient: Dispatch<SetStateAction<ConsultPatient | null>>;
  setLoading: Dispatch<SetStateAction<boolean>>;
  session: TSession | null;
  markStepComplete?: (stepId: TTooltipStep) => void;
  fetchStarredSimulations?: () => Promise<any[]>;
  setSimulations?: Dispatch<SetStateAction<any[]>>;
}

export const useConsultNavigation = ({
  currentView,
  setCurrentView,
  selectedPatient,
  setSelectedPatient,
  setLoading,
  session,
  markStepComplete,
  fetchStarredSimulations,
  setSimulations,
}: ConsultNavigationProps) => {
  const handlePatientSelect = useCallback(
    async (patient: ConsultPatient | null) => {
      if (!patient) return;

      trackEvent(
        "Patient Consult Viewed",
        {
          "Patient ID": patient.clinicOSUUID,
          "Patient Name": patient.name,
          "Procedure Type": patient.procedureType,
          "Visit Type": patient.visitType || "Consultation",
          "Has Photos": patient.hasPhotos,
          "Appointment Time": patient.appointmentTime,
          "Consultation Date": patient.consultationDate,
        },
        session ?? undefined,
      );

      markStepComplete?.(TOOLTIP_STEPS.SELECT_APPOINTMENT);

      // The consult patient already carries its ClinicOS UUID (clinicOSUUID),
      // mapped from the appointment's patient_uuid — no resolution step needed.
      setSelectedPatient(patient);
      requestAnimationFrame(() => setCurrentView("planningView"));
    },
    [markStepComplete, session, setCurrentView, setSelectedPatient],
  );

  const goToPresentationView = useCallback(async () => {
    setCurrentView("presentationView");

    if (
      !selectedPatient?.patientRecordId ||
      !fetchStarredSimulations ||
      !setSimulations
    ) {
      return;
    }

    try {
      setLoading(true);
      const starredSimulations = await fetchStarredSimulations();
      if (starredSimulations?.length) {
        setSimulations((prev: any[]) => {
          const existingIds = new Set(prev.map((sim) => sim.id));
          const merged = starredSimulations.filter(
            (sim) => !existingIds.has(sim.id),
          );
          return [...prev, ...merged];
        });
      }
    } catch (error) {
      console.error("Error fetching presentation data:", error);
    } finally {
      setLoading(false);
    }

    trackEvent(
      "Presentation Mode Button Clicked",
      {
        "Patient ID": selectedPatient?.clinicOSUUID,
        "Patient Name": selectedPatient?.name,
        "From View": "planningView",
      },
      session ?? undefined,
    );
  }, [
    fetchStarredSimulations,
    selectedPatient,
    session,
    setCurrentView,
    setLoading,
    setSimulations,
  ]);

  const handleBack = useCallback(() => {
    if (typeof window !== "undefined" && (window as any).stopVoiceNotesAudio) {
      (window as any).stopVoiceNotesAudio();
    }

    if (currentView === "planningView") {
      setCurrentView("baseView");
      setSelectedPatient(null);
    } else if (currentView === "presentationView") {
      setCurrentView("planningView");
    }
  }, [currentView, setCurrentView, setSelectedPatient]);

  return {
    handlePatientSelect,
    goToPresentationView,
    handleBack,
  };
};
